/**
 * The Designer's transform and structure commands — everything that moves,
 * resizes, groups, copies or removes layers, plus the two commands that replace
 * a whole design (a starter template, a Sticker switch).
 *
 * `designer-parity.ts` holds the content and property commands; this file adds
 * the geometric ones and is the single entry point (`applyDesignerCommand`) the
 * widget, Studio and the HTTP/MCP endpoints all call. Two ideas run through it:
 *
 *  - **A group is a unit.** Select one member and the transform, the copy and
 *    the removal take the whole group. Content edits do not — see the parity
 *    file — because retyping one label of a grouped badge is a normal thing to
 *    want.
 *  - **Millimetres all the way down.** Every box is in the zone's own frame, so
 *    an alignment computed here means the same thing on the print file and on a
 *    tilted mock-up.
 */

import {
  designAreaProblems,
  layersExtent,
  newDesignLayerId,
  normalizeDesignAnswer,
  resolveDesignAreaConfig,
  type DesignAreaAnswer,
  type DesignAreaConfig,
  type DesignBox,
  type DesignLayer,
  type DesignImageLayer,
} from './design-area.js';
import type { Color } from './types.js';
import { stickerMaxSideMm, STICKER_DEFAULTS, STICKER_LIMITS, STICKER_OUTLINES, validStickerSettings, type StickerSettings } from './sticker.js';
import { fitDesignerTemplate } from './designer-templates.js';
import { PRINT_LOCATION_CAPABILITIES } from './print-location.js';
import { DESIGNER_LOOK_CAPABILITIES } from './designer-looks.js';
import { applyDesignerLayout, DESIGNER_LAYOUTS, type DesignerLayoutCommand } from './designer-layout.js';
export { DESIGNER_LAYOUTS } from './designer-layout.js';
export type { DesignerLayoutCommand, DesignerLayoutMode } from './designer-layout.js';
import { applyDesignerArrange, DESIGNER_ARRANGE_CAPABILITIES, DESIGNER_ARRANGEMENTS, type DesignerArrangeCommand } from './designer-arrange.js';
import {
  applyDesignerParityCommand,
  assertEditable,
  assertRemovable,
  AUTHORING_FIELD,
  DESIGNER_PARITY_CAPABILITIES,
  parseDesignerParityCommand,
  type DesignerAuthoring,
  type DesignerParityCommand,
} from './designer-parity.js';

export type DesignerCommand =
  | DesignerParityCommand
  | DesignerLayoutCommand
  | DesignerArrangeCommand
  | (DesignerAuthoring & (
    | { op: 'sticker'; settings: StickerSettings | null }
    | { op: 'align'; ids: string[]; direction: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom'; reference: 'zone' | 'selection' }
    | { op: 'distribute'; ids: string[]; axis: 'x' | 'y' }
    | { op: 'move'; ids: string[]; dx: number; dy: number }
    | { op: 'rotate'; ids: string[]; degrees: number }
    | { op: 'scale'; ids: string[]; factor: number }
    | { op: 'place'; ids: string[]; box: DesignBox }
    | { op: 'group' | 'ungroup' | 'duplicate' | 'delete'; ids: string[] }
    /**
     * `scriptFontId` (optional): a second, handwritten face for the layouts
     * that pair a display letter with a written name (monograms). Absent =
     * `fontId` throughout. Both must be fonts the product allows.
     */
    /**
     * `imageLayerId` (optional): the photo a photo template puts in its slot;
     * absent = the first visible photo. `fill` (optional): the shopper's words
     * by field key (`ime`, `datum`…), so a new layout keeps the name they typed.
     */
    | { op: 'template'; templateId: string; fontId: string; colorway?: DesignerColorway; scriptFontId?: string; imageLayerId?: string; fill?: Record<string, string> }
  ));

export const DESIGNER_CAPABILITIES = {
  starterLayout: { operation: 'starterLayout', modes: ['image-only', 'text-only', 'text-above', 'text-below', 'text-overlay'], fields: ['mode', 'fontId', 'imageLayerId?', 'textLayerId?', 'text?', 'gapMm?', 'sticker?'], gapMm: [0, 30], scope: 'Replaces only the selected print zone. Select existing photo/text IDs or use the first visible layer. Image modes require an existing photo. Preserves chosen photo crop/mask/eraser and text styling. Omitted sticker preserves manufacturing settings; required Sticker cannot be disabled. Use imageFit/imageCrop/text/sticker commands for further settings. Native preview and print; current revision and idempotency required through edit_design_area.' },
  version: 2,
  units: 'mm',
  coordinates: 'Layer centres from zone top-left',
  arrange: DESIGNER_ARRANGE_CAPABILITIES,
  operations: ['starterLayout', 'arrange', 'sticker', 'align', 'distribute', 'move', 'rotate', 'scale', 'place', 'group', 'ungroup', 'duplicate', 'delete', 'template', ...DESIGNER_PARITY_CAPABILITIES.operations],
  place: 'One anchor ID and an absolute box; grouped layers follow its translation, uniform scale and rotation. Ungroup before stretching. A text layer scales its font size with the box — except a fixed one, whose width is only the wrap width; scale that with op: scale.',
  alignment: ['left', 'center', 'right', 'top', 'middle', 'bottom'],
  parity: DESIGNER_PARITY_CAPABILITIES,
  printLocation: PRINT_LOCATION_CAPABILITIES,
  looks: DESIGNER_LOOK_CAPABILITIES,
  whiteRemoval: "{ kind: 'whiteTransparent' } removes all near-white; add connected: true to remove only white connected to the picture border (background only). Native pixel pass, identical preview and print.",
  // Every number here is read from STICKER_LIMITS, so the published capability
  // and the code that enforces it cannot drift apart.
  sticker: {
    borderMm: [STICKER_LIMITS.minBorderMm, STICKER_LIMITS.maxBorderMm],
    bridgeMm: [STICKER_LIMITS.minBridgeMm, STICKER_LIMITS.maxBridgeMm],
    maxSideMm: stickerMaxSideMm(),
    maxBridgedIslands: STICKER_LIMITS.maxBridgedIslands,
    automaticBridges: true,
    outline: { values: STICKER_OUTLINES, default: STICKER_DEFAULTS.outline, legacyOmitted: 'even', maxLinkMm: STICKER_LIMITS.maxLinkMm, description: "'snug' keeps a thin borderMm rim and closes the gaps between letters and pieces only as far as needed to join them (up to maxLinkMm radius; farther pieces get bridges). 'even' grows the whole silhouette by borderMm." },
    imageBorder: { type: 'boolean', default: false, legacyOmitted: true, description: 'false keeps uploaded photo edges unchanged; borders text, icons and emoji, then connects all pieces with white bridges' },
    holePolicy: 'fill',
    cutFile: 'one SVG per sticker zone, registered to the full print side, millimetres',
    contourPxPerMm: [STICKER_LIMITS.minPxPerMm, STICKER_LIMITS.maxPxPerMm],
  },
  templates: ['nameplate', 'monogram', 'celebration', 'dedication', 'badge', 'signature', 'thank-you', 'birthday', 'wedding', 'label', 'stars', 'hearts', 'family', 'anniversary', 'newborn', 'pet', 'team', 'graduation', 'mom', 'dad', 'minimal', 'holiday', 'handmade', 'address',
    'initial-name', 'initial-frame', 'welcome-baby', 'little-one', 'first-christmas', 'love-heart', 'name-arc', 'hug', 'best-friend', 'get-well', 'big-sibling',
    'photo-heart-name', 'photo-circle-name', 'photo-polaroid', 'photo-welcome', 'photo-side-name', 'photo-love', 'photo-birthday', 'photo-friends',
    'photo-full', 'photo-heart', 'photo-circle', 'photo-stars'],
  templateKinds: { text: 'words and shapes only', photo: 'one photo slot (photo-*) without words', 'photo-text': 'one photo slot plus name/date fields' },
  templatePhotos: 'Photo templates (ids starting photo-) put imageLayerId (default: the first visible photo) into a masked cover slot, keeping asset, effects and eraser; the photo crop is reset to the slot. Refused when the design has no photo.',
  templateFill: 'fill: {key: text} replaces keyed template fields (ime, datum, potpis, natpis, inicijal, uloga, godina, mere) with the shopper’s words; too-long words are refused with the product rule.',
  templateFonts: 'fontId sets every line; the optional scriptFontId sets the handwritten lines of initial-name, initial-frame, welcome-baby, little-one, first-christmas, love-heart, hug, best-friend, get-well, big-sibling and the photo-* layouts. Name/date/initial lines are template fields (editOnCard, keyed by label) so they appear on the product card and in "Moji detalji".',
  templateColorways: ['original', 'rose', 'ocean', 'forest', 'sunset', 'mono', 'light'],
  customTemplates: { operation: 'configure_design_template', input: 'change: {template: DesignerTemplate} or change: {removeId: custom:id}', maxPerZone: 24, idPrefix: 'custom:', fields: ['id', 'name', 'description', 'category', 'sizeMm', 'answer'], application: 'Proportional fit with new layer/group IDs, native fonts and assets; one undo step; product rules enforced.' },
  limitations: 'Sticker requires an unmasked flat production zone. Contour grid spacing is not a certified cutter tolerance. Registration/kerf/physical cutter calibration requires human acceptance. Sticker artwork exports as pixels; its cut outline is vector.',
} as const;

/** Which fields each of this file's own ops carries. */
const keys: Record<string, string[]> = {
  starterLayout: ['mode', 'fontId', 'imageLayerId', 'textLayerId', 'text', 'gapMm', 'sticker'],
  arrange: ['variant'],
  sticker: ['settings'],
  align: ['ids', 'direction', 'reference'],
  distribute: ['ids', 'axis'],
  move: ['ids', 'dx', 'dy'],
  rotate: ['ids', 'degrees'],
  scale: ['ids', 'factor'],
  place: ['ids', 'box'],
  group: ['ids'],
  ungroup: ['ids'],
  duplicate: ['ids'],
  delete: ['ids'],
  template: ['templateId', 'fontId', 'colorway', 'scriptFontId', 'imageLayerId', 'fill'],
};

function parsePlacementBox(raw: Record<string, unknown>): void {
  const ids = raw.ids as string[];
  if (ids.length !== 1) throw new Error('Invalid placement box');
  const box = raw.box as Record<string, unknown> | undefined;
  if (!box || typeof box !== 'object' || Array.isArray(box) || !Object.keys(box).every((k) => ['x', 'y', 'w', 'h', 'rotation'].includes(k))) {
    throw new Error('Invalid placement box');
  }
  const sized = ['x', 'y', 'w', 'h'].every((k) => typeof box[k] === 'number' && Number.isFinite(box[k]) && Math.abs(box[k] as number) <= 2000);
  if (!sized || (box.w as number) <= 0 || (box.h as number) <= 0) throw new Error('Invalid placement box');
  if (box.rotation !== undefined && (typeof box.rotation !== 'number' || !Number.isFinite(box.rotation) || Math.abs(box.rotation) > 360)) {
    throw new Error('Invalid placement box');
  }
}

/** Strict, portable authoring schema. Unknown fields and nonfinite numbers fail. */
export function parseDesignerCommand(raw: unknown): DesignerCommand {
  const parity = parseDesignerParityCommand(raw);
  if (parity) return parity;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Expected a Designer command');
  const r = raw as Record<string, unknown>;
  const op = String(r.op);
  if (!keys[op] || Object.keys(r).some((k) => k !== 'op' && k !== AUTHORING_FIELD && !keys[op]!.includes(k))) {
    throw new Error('Unknown Designer command or field');
  }
  if (r[AUTHORING_FIELD] !== undefined && typeof r[AUTHORING_FIELD] !== 'boolean') throw new Error('Invalid authoring flag');
  const number = (key: string, min: number, max: number): void => {
    const value = r[key];
    if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) throw new Error(`Invalid ${key}`);
  };
  if (keys[op]!.includes('ids')) {
    const ids = r.ids;
    const usable = Array.isArray(ids) && ids.length > 0 && ids.length <= 60
      && ids.every((id) => typeof id === 'string' && id && id.length <= 40)
      && new Set(ids).size === ids.length;
    if (!usable) throw new Error('Select 1–60 unique layer IDs');
  }
  if (op === 'sticker' && r.settings !== null) {
    const settings = r.settings as Record<string, unknown> | null;
    if (!validStickerSettings(settings) || Object.keys(settings!).some((k) => !['borderMm', 'bridgeMm', 'imageBorder', 'outline'].includes(k))) throw new Error('Invalid Sticker settings');
  }
  if (op === 'align' && (!['left', 'center', 'right', 'top', 'middle', 'bottom'].includes(String(r.direction)) || !['zone', 'selection'].includes(String(r.reference)))) {
    throw new Error('Invalid alignment');
  }
  if (op === 'distribute' && !['x', 'y'].includes(String(r.axis))) throw new Error('Invalid distribution axis');
  if (op === 'move') { number('dx', -2000, 2000); number('dy', -2000, 2000); }
  if (op === 'rotate') number('degrees', -360, 360);
  if (op === 'scale') number('factor', 0.1, 10);
  if (op === 'place') parsePlacementBox(r);
  if (op === 'starterLayout') {
    if (!DESIGNER_LAYOUTS.some(l => l.id === r.mode)) throw new Error('Invalid starter layout');
    if (typeof r.fontId !== 'string' || !r.fontId || r.fontId.length > 100) throw new Error('Invalid layout font');
    for (const key of ['imageLayerId', 'textLayerId']) if (r[key] !== undefined && (typeof r[key] !== 'string' || !(r[key] as string).length || (r[key] as string).length > 40)) throw new Error('Invalid source layer');
    if (r.text !== undefined && (typeof r.text !== 'string' || r.text.length > 2000)) throw new Error('Invalid layout text');
    if (r.gapMm !== undefined) number('gapMm', 0, 30);
    if (r.sticker !== undefined && typeof r.sticker !== 'boolean') throw new Error('Invalid layout Sticker choice');
  }
  if (op === 'arrange' && !DESIGNER_ARRANGEMENTS.some(a => a.id === r.variant)) throw new Error('Invalid arrangement');
  if (op === 'template') {
    if (r.colorway !== undefined && !DESIGNER_COLORWAYS.some(c => c.id === r.colorway)) throw new Error('Invalid template colorway');
    const known = DESIGNER_CAPABILITIES.templates.includes(r.templateId as never)
      || (typeof r.templateId === 'string' && /^custom:[a-zA-Z0-9_-]{1,60}$/.test(r.templateId));
    if (!known || typeof r.fontId !== 'string' || !r.fontId || r.fontId.length > 100) throw new Error('Invalid template or font');
    if (r.scriptFontId !== undefined && (typeof r.scriptFontId !== 'string' || !r.scriptFontId || r.scriptFontId.length > 100)) throw new Error('Invalid template script font');
    if (r.imageLayerId !== undefined && (typeof r.imageLayerId !== 'string' || !r.imageLayerId || r.imageLayerId.length > 40)) throw new Error('Invalid template photo');
    if (r.fill !== undefined) {
      const fill = r.fill as Record<string, unknown>;
      const ok = fill && typeof fill === 'object' && !Array.isArray(fill) && Object.keys(fill).length <= 12
        && Object.entries(fill).every(([key, value]) => /^[a-z0-9-]{1,40}$/.test(key) && typeof value === 'string' && value.length <= 2000);
      if (!ok) throw new Error('Invalid template fields');
    }
  }
  return structuredClone(raw) as DesignerCommand;
}

// ── The whole-design commands ────────────────────────────────────────────────

function applySticker(config: DesignAreaConfig, answer: DesignAreaAnswer, settings: StickerSettings | null): DesignAreaAnswer {
  const next = structuredClone(answer);
  if (settings && answer.background) throw new Error('Clear the printed background before enabling Sticker');
  if (settings && !config.sticker?.allowed) throw new Error('Sticker is not available for this product');
  if (!settings && config.sticker?.required) throw new Error('This product requires Sticker');
  if (settings) next.sticker = settings; else delete next.sticker;
  return next;
}

function applyStarterTemplate(config: DesignAreaConfig, answer: DesignAreaAnswer, command: Extract<DesignerCommand, { op: 'template' }>): DesignAreaAnswer {
  const { templateId, fontId, authoring, colorway, scriptFontId, imageLayerId, fill } = command;
  // A starter layout throws the current design away, so it answers to the same
  // rule as a delete: an author's permanent layer is not the shopper's to lose.
  assertRemovable(answer.layers, authoring);
  const photo = imageLayerId
    ? answer.layers.find((layer) => layer.id === imageLayerId && layer.kind === 'image')
    : answer.layers.find((layer) => layer.kind === 'image' && layer.visible !== false);
  if (imageLayerId && !photo) throw new Error('The selected photo no longer exists');
  const next = designerTemplate(config, templateId, fontId, colorway, scriptFontId, {
    ...(photo?.kind === 'image' ? { photo } : {}),
    ...(fill ? { fill } : {}),
  });
  // Sticker is a manufacturing choice about the product, not part of the
  // artwork — a new layout must not silently switch it off.
  if (answer.sticker) next.sticker = answer.sticker;
  else if (config.sticker?.required) next.sticker = config.sticker.defaults ?? STICKER_DEFAULTS;
  const problem = designAreaProblems(config, next)[0];
  if (problem) throw new Error(problem.detail);
  return next;
}

// ── Transforms ───────────────────────────────────────────────────────────────

function applyDuplicate(next: DesignAreaAnswer, selected: DesignLayer[]): void {
  // Copies get fresh ids, and a copied GROUP gets one fresh group id shared by
  // its members, so the copy is a group of its own rather than a second handle
  // on the original. Offset 2 mm so it is visibly a copy.
  const groups = new Map<string, string>();
  const copies = selected.map((layer) => {
    if (layer.groupId && !groups.has(layer.groupId)) groups.set(layer.groupId, newDesignLayerId());
    return {
      ...layer,
      id: newDesignLayerId(),
      groupId: layer.groupId ? groups.get(layer.groupId) : undefined,
      box: { ...layer.box, x: layer.box.x + 2, y: layer.box.y + 2 },
    };
  });
  next.layers.push(...copies);
}

function applyPlace(answer: DesignAreaAnswer, selected: DesignLayer[], command: { ids: string[]; box: DesignBox }): void {
  // One layer is the anchor; the rest of its group follow the same similarity
  // transform (translate, uniform scale, rotate) that took the anchor's old box
  // to the new one. Anything else would shear a group out of shape.
  const anchor = answer.layers.find((layer) => layer.id === command.ids[0])!;
  const old = anchor.box, box = command.box;
  const sx = box.w / old.w, sy = box.h / old.h;
  const delta = (box.rotation ?? 0) - (old.rotation ?? 0);
  const angle = delta * Math.PI / 180;
  if (selected.length > 1 && Math.abs(sx - sy) > 0.02) throw new Error('Ungroup layers before stretching; groups keep their proportions');
  for (const layer of selected) {
    const x = (layer.box.x - old.x) * sx, y = (layer.box.y - old.y) * sx;
    layer.box = {
      ...layer.box,
      x: box.x + x * Math.cos(angle) - y * Math.sin(angle),
      y: box.y + x * Math.sin(angle) + y * Math.cos(angle),
      w: layer.box.w * sx,
      h: layer.box.h * (selected.length > 1 ? sx : sy),
      rotation: ((layer.box.rotation ?? 0) + delta) % 360,
    };
    // A wider AUTO box means bigger letters: the box hugs the text, so the only
    // way it could have grown is that the shopper asked for more type. A FIXED
    // box is the opposite — its width is the wrap width, and dragging a side
    // handle re-flows the same letters into more or fewer lines. Scaling the em
    // there would make the handle do two things at once, neither of them Zazzle's.
    if (layer.kind === 'text' && layer.boxMode !== 'fixed') { layer.sizeMm *= sx; if (layer.textWidthMm) layer.textWidthMm *= sx; }
    if (layer.kind === 'shape' && layer.stroke) layer.stroke.widthMm *= sx;
  }
}

function applyRotate(selected: DesignLayer[], degrees: number, cx: number, cy: number): void {
  // The selection turns about its own centre: each centre orbits, each layer
  // turns on the spot by the same amount.
  const angle = degrees * Math.PI / 180;
  for (const layer of selected) {
    const x = layer.box.x - cx, y = layer.box.y - cy;
    layer.box.x = cx + x * Math.cos(angle) - y * Math.sin(angle);
    layer.box.y = cy + x * Math.sin(angle) + y * Math.cos(angle);
    layer.box.rotation = ((layer.box.rotation ?? 0) + degrees) % 360;
  }
}

function applyScale(selected: DesignLayer[], factor: number, cx: number, cy: number): void {
  for (const layer of selected) {
    layer.box = {
      ...layer.box,
      x: cx + (layer.box.x - cx) * factor,
      y: cy + (layer.box.y - cy) * factor,
      w: layer.box.w * factor,
      h: layer.box.h * factor,
    };
    if (layer.kind === 'text') { layer.sizeMm *= factor; if (layer.textWidthMm) layer.textWidthMm *= factor; }
    if (layer.kind === 'shape' && layer.stroke) layer.stroke.widthMm *= factor;
  }
}

/** Align and distribute both treat a group as one movable unit. */
function unitsOf(selected: DesignLayer[]): DesignLayer[][] {
  const byUnit = new Map<string, DesignLayer[]>();
  for (const layer of selected) {
    const key = layer.groupId ? `group:${layer.groupId}` : `layer:${layer.id}`;
    const unit = byUnit.get(key) ?? [];
    unit.push(layer);
    byUnit.set(key, unit);
  }
  return [...byUnit.values()];
}

function applyAlign(
  units: DesignLayer[][],
  reference: { left: number; top: number; right: number; bottom: number },
  direction: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom',
): void {
  for (const unit of units) {
    const extent = layersExtent(unit);
    let dx = 0, dy = 0;
    switch (direction) {
      case 'left': dx = reference.left - extent.left; break;
      case 'right': dx = reference.right - extent.right; break;
      case 'top': dy = reference.top - extent.top; break;
      case 'bottom': dy = reference.bottom - extent.bottom; break;
      case 'center': dx = (reference.left + reference.right - extent.left - extent.right) / 2; break;
      case 'middle': dy = (reference.top + reference.bottom - extent.top - extent.bottom) / 2; break;
    }
    for (const layer of unit) { layer.box.x += dx; layer.box.y += dy; }
  }
}

function applyDistribute(units: DesignLayer[][], bounds: { left: number; top: number; right: number; bottom: number }, axis: 'x' | 'y'): void {
  if (units.length < 3) throw new Error('Select at least three separate layers or groups to distribute');
  const min = axis === 'x' ? 'left' : 'top', max = axis === 'x' ? 'right' : 'bottom';
  units.sort((a, b) => layersExtent(a)[min] - layersExtent(b)[min]);
  // Equal GAPS, not equal centres: that is what "evenly spaced" looks like when
  // the items are different widths, and it is what Zazzle does.
  const occupied = units.reduce((total, unit) => {
    const extent = layersExtent(unit);
    return total + extent[max] - extent[min];
  }, 0);
  const gap = (bounds[max] - bounds[min] - occupied) / (units.length - 1);
  if (gap < 0) throw new Error('There is not enough space to distribute without overlap');
  let at = bounds[min];
  for (const unit of units) {
    const extent = layersExtent(unit);
    const delta = at - extent[min];
    for (const layer of unit) layer.box[axis] += delta;
    at += extent[max] - extent[min] + gap;
  }
}

/** Shared authoring operation used by Designer, Studio and HTTP/MCP. Pure + atomic. */
export function applyDesignerCommand(config: DesignAreaConfig, answer: DesignAreaAnswer, raw: unknown): DesignAreaAnswer {
  const command = parseDesignerCommand(raw);
  const cfg = resolveDesignAreaConfig(config);
  const parity = parseDesignerParityCommand(command);
  if (parity) return applyDesignerParityCommand(config, answer, parity);
  if (command.op === 'starterLayout') return applyDesignerLayout(config, answer, command);
  if (command.op === 'arrange') return applyDesignerArrange(config, answer, command);

  const next = structuredClone(answer);
  if (command.op === 'sticker') return applySticker(config, answer, command.settings);
  if (command.op === 'template') return applyStarterTemplate(config, answer, command);
  if (!('ids' in command)) throw new Error('Invalid Designer operation');

  const ids = new Set(command.ids);
  if (command.ids.some((id) => !answer.layers.some((layer) => layer.id === id))) throw new Error('A selected layer no longer exists');
  // A group is always a unit for transforms, duplication and removal — except
  // when the command is `group` itself, which is how a group is made.
  if (command.op !== 'group') {
    const groups = new Set(answer.layers.filter((layer) => ids.has(layer.id) && layer.groupId).map((layer) => layer.groupId));
    answer.layers.filter((layer) => layer.groupId && groups.has(layer.groupId)).forEach((layer) => ids.add(layer.id));
  }
  const selected = next.layers.filter((layer) => ids.has(layer.id));
  assertEditable(selected, command.authoring);
  if (command.op === 'delete') assertRemovable(selected, command.authoring);
  if (selected.some((layer) => layer.locked)) throw new Error('Unlock selected layers before editing them');

  const bounds = layersExtent(selected);
  const cx = (bounds.left + bounds.right) / 2, cy = (bounds.top + bounds.bottom) / 2;
  if (command.op === 'delete') next.layers = next.layers.filter((layer) => !ids.has(layer.id));
  if (command.op === 'duplicate') applyDuplicate(next, selected);
  if (command.op === 'group') {
    if (selected.length < 2) throw new Error('Select at least two layers to group');
    const id = newDesignLayerId();
    for (const layer of selected) layer.groupId = id;
  }
  if (command.op === 'ungroup') {
    // Ungrouping one member releases the whole group; a two-member "group"
    // with one member still in it is not a thing.
    const groups = new Set(selected.map((layer) => layer.groupId).filter(Boolean));
    next.layers
      .filter((layer) => ids.has(layer.id) || (layer.groupId && groups.has(layer.groupId)))
      .forEach((layer) => { delete layer.groupId; });
  }
  if (command.op === 'move') for (const layer of selected) { layer.box.x += command.dx; layer.box.y += command.dy; }
  if (command.op === 'place') applyPlace(answer, selected, command);
  if (command.op === 'rotate') applyRotate(selected, command.degrees, cx, cy);
  if (command.op === 'scale') applyScale(selected, command.factor, cx, cy);
  if (command.op === 'align') {
    const reference = command.reference === 'selection'
      ? bounds
      : { left: cfg.safeMarginMm, top: cfg.safeMarginMm, right: cfg.sizeMm.w - cfg.safeMarginMm, bottom: cfg.sizeMm.h - cfg.safeMarginMm };
    applyAlign(unitsOf(selected), reference, command.direction);
  }
  if (command.op === 'distribute') applyDistribute(unitsOf(selected), bounds, command.axis);

  const normalized = normalizeDesignAnswer(next)!;
  // Resolution, asset provenance and library membership need a database this
  // function does not have; the commit checks them. Everything structural is
  // this function's own responsibility and is enforced here.
  const structural = designAreaProblems(config, normalized).filter((p) => !['low-resolution', 'asset-unknown', 'library-not-allowed'].includes(p.code));
  if (structural.length) throw new Error(structural[0]!.detail);
  return normalized;
}

export const DESIGNER_COLORWAYS = [
  { id: 'original', name: 'Original', ink: '#202124', accent: '#ff8fab' },
  { id: 'rose', name: 'Ruža', ink: '#5c1535', accent: '#eea9bd' },
  { id: 'ocean', name: 'More', ink: '#163852', accent: '#74bccc' },
  { id: 'forest', name: 'Šuma', ink: '#233e32', accent: '#aac6a4' },
  { id: 'sunset', name: 'Zalazak', ink: '#5c2c20', accent: '#f3b37d' },
  { id: 'mono', name: 'Crno-belo', ink: '#171717', accent: '#d9d9d9' },
  // For dark products (a navy or black shirt): white words, a soft pink accent.
  { id: 'light', name: 'Svetlo', ink: '#ffffff', accent: '#ffc2d1' },
] as const;
export type DesignerColorway = typeof DESIGNER_COLORWAYS[number]['id'];

export const DESIGNER_TEMPLATES = [
  { id: 'family', name: 'Naša porodica', category: 'Porodica', description: 'Imena ispod tri srca' },
  { id: 'anniversary', name: 'Naše godine', category: 'Ljubav', description: 'Godina i lična posveta' },
  { id: 'newborn', name: 'Dobro došla, bebo', category: 'Porodica', description: 'Ime, datum i mali simbol' },
  { id: 'pet', name: 'Moj najbolji drug', category: 'Ljubimci', description: 'Ime ljubimca i posveta' },
  { id: 'team', name: 'Tim i broj', category: 'Sport', description: 'Sportski raspored sa imenom i brojem' },
  { id: 'graduation', name: 'Veliki uspeh', category: 'Proslave', description: 'Čestitka za diplomiranje' },
  { id: 'mom', name: 'Za najbolju mamu', category: 'Porodica', description: 'Posveta sa srcem' },
  { id: 'dad', name: 'Za najboljeg tatu', category: 'Porodica', description: 'Posveta sa zvezdom' },
  { id: 'minimal', name: 'Minimalni potpis', category: 'Imena', description: 'Čista tipografija i mali detalj' },
  { id: 'holiday', name: 'Praznična poruka', category: 'Proslave', description: 'Zvezde i praznična čestitka' },
  { id: 'handmade', name: 'Ručno i od srca', category: 'Posao', description: 'Potpis malog brenda' },
  { id: 'address', name: 'Poklon etiketa', category: 'Posao', description: 'Tri uredna reda za paket' },
  { id: 'nameplate', name: 'Ime i srce', category: 'Imena', description: 'Ime sa malim srcem' },
  { id: 'monogram', name: 'Monogram', category: 'Imena', description: 'Inicijal u krugu' },
  { id: 'celebration', name: 'Proslava', category: 'Pokloni', description: 'Čestitka sa zvezdama' },
  { id: 'dedication', name: 'Posveta', category: 'Pokloni', description: 'Naslov i lična poruka' },
  { id: 'badge', name: 'Bedž', category: 'Oblici', description: 'Ime na obojenoj podlozi' },
  { id: 'signature', name: 'Potpis', category: 'Imena', description: 'Dva reda, jedan potpis' },
  { id: 'thank-you', name: 'Hvala', category: 'Pokloni', description: 'Mala poruka zahvalnosti' },
  { id: 'birthday', name: 'Rođendan', category: 'Proslave', description: 'Ime, godine i zvezda' },
  { id: 'wedding', name: 'Naš dan', category: 'Proslave', description: 'Imena i datum venčanja' },
  { id: 'label', name: 'Etiketa', category: 'Posao', description: 'Naziv i kratka poruka' },
  { id: 'stars', name: 'Zvezdana mreža', category: 'Ponavljanje', description: 'Izmenjive zvezde preko celog polja' },
  { id: 'hearts', name: 'Srca u nizu', category: 'Ponavljanje', description: 'Ponavljanje sa pomerenim redovima' },
  // Teddy, baby and gift layouts — our own compositions for small chest
  // prints (a plush bear's shirt is about 7.5 × 6.5 cm). Monogram layouts pair
  // the display font with the optional handwritten `scriptFontId`.
  { id: 'initial-name', name: 'Inicijal i ime', category: 'Meda i beba', description: 'Veliko početno slovo sa imenom preko njega' },
  { id: 'initial-frame', name: 'Inicijal u venčiću', category: 'Meda i beba', description: 'Slovo u krugu, ime i datum ispod' },
  { id: 'welcome-baby', name: 'Dobrodošlica', category: 'Meda i beba', description: 'Ime bebe, datum, težina i dužina' },
  { id: 'little-one', name: 'Mali medo', category: 'Meda i beba', description: 'Srce, „mali” i ime' },
  { id: 'first-christmas', name: 'Prvi Božić', category: 'Meda i beba', description: 'Zvezde, poruka i godina' },
  { id: 'love-heart', name: 'Volim te', category: 'Ljubav', description: 'Poruka u velikom srcu i ime' },
  { id: 'name-arc', name: 'Ime u luku', category: 'Imena', description: 'Ime zakrivljeno iznad zvezde' },
  { id: 'hug', name: 'Zagrljaj za tebe', category: 'Pokloni', description: 'Veliki zagrljaj i potpis' },
  { id: 'best-friend', name: 'Najbolji drug', category: 'Pokloni', description: 'Ime između dve zvezde' },
  { id: 'get-well', name: 'Ozdravi brzo', category: 'Pokloni', description: 'Topla poruka i srce' },
  { id: 'big-sibling', name: 'Stariji brat / sestra', category: 'Meda i beba', description: 'Uloga u porodici i ime' },
  // Photo layouts: the shopper's own photo goes into the slot (shaped by a
  // mask), their name into the name field. Our own compositions.
  { id: 'photo-heart-name', name: 'Slika u srcu i ime', category: 'Ljubav', description: 'Tvoja slika u srcu, ime ispod', kind: 'photo-text' },
  { id: 'photo-circle-name', name: 'Ime u luku', category: 'Imena', description: 'Okrugla slika, ime u luku iznad', kind: 'photo-text' },
  { id: 'photo-polaroid', name: 'Polaroid', category: 'Pokloni', description: 'Slika u ramu i potpis', kind: 'photo-text' },
  { id: 'photo-welcome', name: 'Slika, ime i datum', category: 'Meda i beba', description: 'Za bebu, rođendan ili uspomenu', kind: 'photo-text' },
  { id: 'photo-side-name', name: 'Slika i ime pored', category: 'Imena', description: 'Okrugla slika levo, ime desno', kind: 'photo-text' },
  { id: 'photo-love', name: 'Volim te sa slikom', category: 'Ljubav', description: 'Poruka, slika u srcu i ime', kind: 'photo-text' },
  { id: 'photo-birthday', name: 'Rođendan sa slikom', category: 'Proslave', description: 'Slika, zvezde, čestitka i ime', kind: 'photo-text' },
  { id: 'photo-friends', name: 'Najbolji drugari', category: 'Pokloni', description: 'Zajednička slika i natpis', kind: 'photo-text' },
  { id: 'photo-full', name: 'Cela slika', category: 'Slika', description: 'Slika preko celog polja', kind: 'photo' },
  { id: 'photo-heart', name: 'Slika u srcu', category: 'Slika', description: 'Slika isečena u obliku srca', kind: 'photo' },
  { id: 'photo-circle', name: 'Slika u krugu', category: 'Slika', description: 'Okrugla slika', kind: 'photo' },
  { id: 'photo-stars', name: 'Slika i zvezde', category: 'Slika', description: 'Okrugla slika među zvezdama', kind: 'photo' },
] as const;

/** What a template holds, for the Slika / Tekst / Slika + tekst filter. */
export type DesignerTemplateKind = 'photo' | 'text' | 'photo-text';

/** Shown when a photo template is applied to a design with no photo. */
export const TEMPLATE_NEEDS_PHOTO = 'Prvo dodajte sliku za ovaj šablon.';

export function designerTemplateKind(template: { id: string; kind?: DesignerTemplateKind; answer?: DesignAreaAnswer }): DesignerTemplateKind {
  if (template.kind) return template.kind;
  if (template.answer) {
    const photo = template.answer.layers.some((layer) => layer.kind === 'image');
    const words = template.answer.layers.some((layer) => layer.kind === 'text');
    return photo ? (words ? 'photo-text' : 'photo') : 'text';
  }
  return 'text';
}

/** A template field's key from its label: "Ime" → "ime", shared across products. */
function slugKey(label: string): string {
  return label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
}

/** Original starter layouts, proportional inside any compatible physical zone. */
export interface DesignerTemplateOptions {
  /** The shopper's photo for a photo slot (kept: asset, effects, eraser). */
  photo?: DesignImageLayer;
  /** No photo yet: draw the slot as a soft shape, for a catalogue thumbnail only. */
  placeholder?: boolean;
  /** Words by template field key, replacing the sample text ("Mila" → their name). */
  fill?: Record<string, string>;
}

export function designerTemplate(config: DesignAreaConfig, id: string, fontId: string, colorway: DesignerColorway = 'original', scriptFontId: string = fontId, options: DesignerTemplateOptions = {}): DesignAreaAnswer {
  if (id.startsWith('custom:')) {
    if (colorway !== 'original') throw new Error('Shop-owned templates preserve their authored colours');
    const template = config.templates?.find((candidate) => candidate.id === id);
    if (!template) throw new Error('Unknown custom template');
    return fillTemplateFields(config, fitDesignerTemplate(config, template), options.fill);
  }
  const cfg = resolveDesignAreaConfig(config);
  if (!DESIGNER_TEMPLATES.some((template) => template.id === id)) throw new Error('Unknown template');
  // Every layout below is drawn on a nominal 75 × 50 mm sheet; `k` is the one
  // factor that carries it, proportionally, into this product's zone.
  const margin = Math.max(cfg.safeMarginMm, 4);
  const k = Math.min((cfg.sizeMm.w - margin * 2) / 75, (cfg.sizeMm.h - margin * 2) / 50);
  if (k <= 0) throw new Error('Print zone is too small for this template');
  const layers: DesignLayer[] = [];
  const box = (x: number, y: number, w: number, h: number): DesignBox =>
    ({ x: cfg.sizeMm.w / 2 + (x - 37.5) * k, y: cfg.sizeMm.h / 2 + (y - 25) * k, w: w * k, h: h * k });
  /** Layers painted in the colourway's accent rather than its ink (a monogram letter). */
  const accentText = new Set<string>();
  /** Layers that keep their colour in every colourway (a polaroid's white card and its caption). */
  const fixedColour = new Set<string>();
  const text = (value: string, x: number, y: number, w: number, h: number, size: number, style: { script?: boolean; accent?: boolean; bend?: number; field?: string; ink?: string } = {}): void => {
    const layerId = newDesignLayerId();
    layers.push({
      id: layerId, kind: 'text', text: value, fontId: style.script ? scriptFontId : fontId,
      color: style.ink ? { hex: style.ink } : style.accent ? { hex: DESIGNER_COLORWAYS[0].accent } : cfg.text.palette[0]!,
      sizeMm: size * k, box: box(x, y, w, h), align: 'center',
      // A bend needs the straight width to become a radius. The designer
      // measures it exactly on first paint; until then (a catalogue card, a
      // print from the API) a close estimate keeps the arc an arc.
      ...(style.bend ? { bend: style.bend, textWidthMm: Array.from(value).length * size * k * 0.62 } : {}),
      // The shopper's own words: a template field, linked across products by
      // the same key ("Moji detalji"), editable straight from the product card.
      ...(style.field ? { template: { label: style.field, key: slugKey(style.field), editOnCard: true } } : {}),
    });
    if (style.accent) accentText.add(layerId);
    if (style.ink) fixedColour.add(layerId);
  };
  /** A white card under a photo; stays white in every colourway. */
  const card = (x: number, y: number, w: number, h: number): void => {
    const white = cfg.shape.palette.find((colour) => colour.hex.toLowerCase() === '#ffffff');
    layers.push({ id: newDesignLayerId(), kind: 'shape', shape: 'roundRect', fill: white ?? accentFill(), stroke: { color: { hex: '#d9d9e3' }, widthMm: 0.35 * k }, box: box(x, y, w, h) });
    fixedColour.add(layers.at(-1)!.id);
  };
  const accentFill = (): Color | undefined => cfg.shape.palette.find((colour) => colour.hex === '#ff8fab') ?? cfg.shape.palette[0];
  const shape = (kind: 'heart' | 'ellipse' | 'star5' | 'roundRect', x: number, y: number, w: number, h: number): void => {
    layers.push({ id: newDesignLayerId(), kind: 'shape', shape: kind, fill: accentFill(), box: box(x, y, w, h) });
  };
  /**
   * The photo slot: the shopper's own picture, filling the slot (`cover`) and
   * cut to the slot's shape. Its crop is dropped because the slot has its own
   * proportions; the asset, effects and eraser strokes travel with it.
   */
  const photoSlot = (x: number, y: number, w: number, h: number, mask?: 'heart' | 'ellipse' | 'roundRect'): void => {
    if (options.photo) {
      const photo = structuredClone(options.photo);
      photo.id = newDesignLayerId();
      delete photo.groupId; delete photo.pattern; delete photo.crop;
      photo.visible = true; photo.locked = false; photo.fit = 'cover';
      photo.box = box(x, y, w, h);
      if (mask) photo.maskShape = mask; else delete photo.maskShape;
      layers.push(photo);
      return;
    }
    if (!options.placeholder) throw new Error(TEMPLATE_NEEDS_PHOTO);
    layers.push({ id: newDesignLayerId(), kind: 'shape', shape: mask ?? 'roundRect', fill: accentFill(), opacity: 0.3, box: box(x, y, w, h) });
    placeholders.add(layers.at(-1)!.id);
  };
  const placeholders = new Set<string>();
  switch (id) {
    case 'family': shape('heart',25,10,7,7); shape('heart',37.5,9,10,9); shape('heart',50,10,7,7); text('Naša porodica',37.5,26,70,12,8); text('Ana · Marko · Mila',37.5,41,68,8,5); break;
    case 'anniversary': text('10',37.5,17,38,23,18); shape('heart',14,18,9,9); shape('heart',61,18,9,9); text('godina zajedno',37.5,35,70,10,6); text('I tek smo počeli',37.5,45,65,7,4); break;
    case 'newborn': shape('star5',37.5,9,10,10); text('Mila',37.5,25,68,14,11); text('12. 09. 2026. · 3.250 g',37.5,41,72,8,4.5); break;
    case 'pet': shape('heart',15,14,12,11); text('Luna',45,15,45,15,10); text('Moj najbolji drug',37.5,36,70,10,6); break;
    case 'team': text('TVOJ TIM',37.5,8,70,9,6); text('24',37.5,27,50,25,20); text('MARKO',37.5,44,70,8,5); break;
    case 'graduation': shape('star5',12,10,10,10); shape('star5',63,10,10,10); text('BRAVO!',37.5,22,66,17,12); text('Diplomac 2026',37.5,41,70,9,5); break;
    case 'mom': text('Najbolja',37.5,9,65,10,6); text('MAMA',37.5,25,70,17,12); shape('heart',37.5,42,10,9); break;
    case 'dad': text('Najbolji',37.5,9,65,10,6); text('TATA',37.5,25,70,17,12); shape('star5',37.5,42,10,9); break;
    case 'minimal': text('Ana',37.5,19,70,18,13); shape('roundRect',37.5,32,30,1.5); text('Jedna posebna priča',37.5,43,70,8,4.5); break;
    case 'holiday': shape('star5',14,10,8,8); shape('star5',61,10,8,8); text('Srećni praznici',37.5,25,72,13,8); text('Od naše porodice',37.5,42,70,8,4.5); break;
    case 'handmade': shape('heart',37.5,9,10,9); text('RUČNI RAD',37.5,25,70,13,8); text('Napravljeno za tebe',37.5,42,70,8,4.5); break;
    case 'address': text('ZA TEBE',37.5,10,68,12,8); shape('roundRect',37.5,23,58,1.5); text('Ime i prezime',37.5,33,70,9,5); text('Sa ljubavlju, Ana',37.5,44,70,8,4.5); break;
    case 'thank-you': shape('heart', 37.5, 10, 10, 9); text('Hvala ti', 37.5, 27, 68, 14, 10); text('Od srca', 37.5, 42, 65, 8, 5); break;
    case 'birthday': text('Srećan rođendan', 37.5, 9, 70, 9, 6); text('18', 37.5, 28, 40, 23, 18); shape('star5', 12, 30, 10, 10); shape('star5', 63, 30, 10, 10); break;
    case 'wedding': text('Ana & Marko', 37.5, 13, 70, 14, 9); shape('heart', 37.5, 28, 8, 7); text('12. 09. 2026.', 37.5, 41, 65, 8, 5); break;
    case 'label': text('TVOJ BREND', 37.5, 17, 70, 15, 9); text('Napravljeno sa ljubavlju', 37.5, 35, 70, 9, 4.5); break;
    case 'stars': shape('star5', 37.5, 25, 12, 12); layers[0]!.pattern = { mode: 'mirror', gapXmm: 6 * k, gapYmm: 6 * k }; break;
    case 'hearts': shape('heart', 37.5, 25, 12, 10); layers[0]!.pattern = { mode: 'half-brick', gapXmm: 6 * k, gapYmm: 6 * k }; break;
    case 'nameplate': shape('heart', 37.5, 13, 12, 11); text('Tvoje ime', 37.5, 32, 65, 15, 9); break;
    case 'monogram': shape('ellipse', 37.5, 25, 42, 42); text('A', 37.5, 25, 34, 30, 23); break;
    case 'celebration': shape('star5', 10, 13, 10, 10); shape('star5', 65, 13, 10, 10); text('Srećan dan!', 37.5, 27, 70, 14, 9); text('Od srca', 37.5, 42, 55, 9, 5); break;
    case 'dedication': text('Za tebe', 37.5, 15, 68, 14, 10); text('Najlepše uspomene', 37.5, 34, 70, 10, 5); break;
    case 'badge': shape('roundRect', 37.5, 25, 70, 34); text('Tvoje ime', 37.5, 25, 62, 15, 8); break;
    case 'signature': text('Ana & Marko', 37.5, 19, 70, 16, 9); text('Zauvek zajedno', 37.5, 36, 65, 9, 5); break;
    // ── Teddy, baby and gifts ──
    case 'initial-name': text('M', 37.5, 25, 44, 46, 40, { accent: true, field: 'Inicijal' }); text('Mila', 37.5, 26, 74, 18, 14, { script: true, field: 'Ime' }); break;
    case 'initial-frame': shape('ellipse', 37.5, 19, 30, 30); text('M', 37.5, 19, 24, 24, 17, { field: 'Inicijal' }); text('Mila', 37.5, 39, 70, 10, 7, { script: true, field: 'Ime' }); text('12. 09. 2026.', 37.5, 46.5, 60, 5, 3.2, { field: 'Datum' }); break;
    case 'welcome-baby': text('Dobro došla na svet', 37.5, 7, 70, 7, 4.2); text('Mila', 37.5, 21, 72, 16, 12, { script: true, field: 'Ime' }); shape('star5', 37.5, 33, 5, 5); text('12. 09. 2026.', 37.5, 40, 70, 6, 3.6, { field: 'Datum' }); text('3.250 g · 51 cm', 37.5, 46, 70, 5, 3.2, { field: 'Mere' }); break;
    case 'little-one': shape('heart', 37.5, 11, 12, 11); text('mali', 37.5, 24, 40, 8, 5.5, { script: true }); text('MARKO', 37.5, 36, 70, 14, 10, { field: 'Ime' }); break;
    case 'first-christmas': shape('star5', 14, 10, 8, 8); shape('star5', 61, 10, 8, 8); shape('star5', 37.5, 7, 5, 5); text('Moj prvi Božić', 37.5, 24, 72, 12, 8, { script: true }); text('2026', 37.5, 40, 40, 11, 8, { field: 'Godina' }); break;
    case 'love-heart': shape('heart', 37.5, 23, 44, 40); text('Volim te', 37.5, 21, 38, 12, 7, { script: true }); text('Ana', 37.5, 47, 60, 6, 4, { field: 'Ime' }); break;
    case 'name-arc': text('LUKA', 37.5, 17, 70, 22, 11, { bend: 45, field: 'Ime' }); shape('star5', 37.5, 36, 14, 14); break;
    case 'hug': text('Šaljem ti', 37.5, 9, 60, 8, 5, { script: true }); text('ZAGRLJAJ', 37.5, 24, 72, 15, 10); shape('heart', 37.5, 36, 6, 6); text('od Ane', 37.5, 44, 60, 7, 4.5, { script: true, field: 'Potpis' }); break;
    case 'best-friend': shape('star5', 10, 25, 9, 9); shape('star5', 65, 25, 9, 9); text('Najbolji drug', 37.5, 14, 55, 8, 5); text('Filip', 37.5, 30, 52, 16, 11, { script: true, field: 'Ime' }); break;
    case 'get-well': shape('heart', 37.5, 11, 11, 10); text('Ozdravi brzo', 37.5, 27, 72, 13, 9, { script: true }); text('Mislimo na tebe', 37.5, 42, 65, 7, 4.5); break;
    case 'big-sibling': text('stariji', 37.5, 11, 50, 9, 6, { script: true }); text('BRAT', 37.5, 26, 70, 16, 13, { field: 'Uloga' }); text('Nikola', 37.5, 42, 60, 9, 6, { script: true, field: 'Ime' }); break;
    // ── Photo + words ──
    case 'photo-heart-name': photoSlot(37.5, 18.5, 31, 29, 'heart'); text('Mila', 37.5, 42, 70, 11, 8, { script: true, field: 'Ime' }); break;
    case 'photo-circle-name': text('MILA', 37.5, 8.5, 62, 13, 7.5, { bend: 40, field: 'Ime' }); photoSlot(37.5, 31, 30, 30, 'ellipse'); break;
    case 'photo-polaroid': card(37.5, 25, 42, 49); photoSlot(37.5, 20.5, 36, 33); text('Naš dan', 37.5, 43.5, 38, 7, 5, { script: true, field: 'Potpis', ink: '#202124' }); break;
    case 'photo-welcome': photoSlot(37.5, 15, 42, 27, 'roundRect'); text('Mila', 37.5, 35.5, 70, 11, 8, { script: true, field: 'Ime' }); text('12. 09. 2026.', 37.5, 45.5, 60, 5, 3.4, { field: 'Datum' }); break;
    case 'photo-side-name': photoSlot(19, 25, 32, 32, 'ellipse'); text('Mila', 55, 21, 38, 13, 9, { script: true, field: 'Ime' }); text('zauvek u srcu', 55, 32.5, 38, 6, 3.8); break;
    case 'photo-love': text('Volim te', 37.5, 6.5, 50, 9, 6, { script: true }); photoSlot(37.5, 25.5, 29, 27, 'heart'); text('Ana', 37.5, 44.5, 60, 8, 5.5, { field: 'Ime' }); break;
    case 'photo-birthday': shape('star5', 12, 19, 9, 9); shape('star5', 63, 19, 9, 9); photoSlot(37.5, 18.5, 28, 28, 'ellipse'); text('Srećan rođendan', 37.5, 38.5, 70, 8, 5.5, { script: true }); text('Luka', 37.5, 46, 50, 6, 4.2, { field: 'Ime' }); break;
    case 'photo-friends': photoSlot(37.5, 18.5, 46, 31, 'roundRect'); text('NAJBOLJI DRUGARI', 37.5, 40.5, 72, 8, 5, { field: 'Natpis' }); shape('heart', 37.5, 47.3, 4, 3.6); break;
    // ── Photo only ──
    case 'photo-full': photoSlot(37.5, 25, 75, 50); break;
    case 'photo-heart': photoSlot(37.5, 25, 53, 49, 'heart'); break;
    case 'photo-circle': photoSlot(37.5, 25, 48, 48, 'ellipse'); break;
    case 'photo-stars': shape('star5', 12, 12, 10, 10); shape('star5', 63, 12, 10, 10); shape('star5', 16, 39, 7, 7); shape('star5', 59, 39, 7, 7); photoSlot(37.5, 25, 42, 42, 'ellipse'); break;
  }
  // The shopper's own words replace the sample ones, field by field.
  for (const layer of layers) {
    const key = layer.template?.key;
    if (layer.kind === 'text' && key && options.fill?.[key]?.trim()) layer.text = options.fill[key]!;
  }
  const palette = DESIGNER_COLORWAYS.find(item => item.id === colorway);
  if (!palette) throw new Error('Invalid template colorway');
  if (colorway !== 'original') for (const layer of layers) {
    if (fixedColour.has(layer.id)) continue;
    if (layer.kind === 'text') layer.color = { hex: accentText.has(layer.id) ? palette.accent : palette.ink };
    if (layer.kind === 'shape') layer.fill = { hex: palette.accent };
  }
  const answer: DesignAreaAnswer = { v: 1, layers };
  // Judged without the Sticker rule: the caller decides whether the layout
  // arrives with Sticker on, and an empty-sticker complaint here is noise.
  // A thumbnail's placeholder is not the shopper's design and is not judged —
  // but the product must accept photos for a photo template to be offered.
  if (placeholders.size && !cfg.allow.image) throw new Error('This product does not accept photos');
  const judged = placeholders.size ? { ...answer, layers: layers.filter((layer) => !placeholders.has(layer.id)) } : answer;
  const problems = designAreaProblems({ ...config, sticker: undefined }, judged).filter((p) => !['low-resolution', 'asset-unknown', 'library-not-allowed'].includes(p.code));
  // The shopper's own words can be too long for a field: say so plainly.
  const wordy = options.fill && problems.find((p) => p.code === 'text-too-long' || p.code === 'too-many-lines');
  if (wordy) throw new Error(wordy.detail);
  if (problems.length) throw new Error('This template is not compatible with the product’s fonts, sizes or allowed tools');
  return answer;
}

/** A shop template's keyed text fields take the shopper's words too. */
function fillTemplateFields(config: DesignAreaConfig, answer: DesignAreaAnswer, fill?: Record<string, string>): DesignAreaAnswer {
  if (!fill) return answer;
  for (const layer of answer.layers) {
    const key = layer.template?.key;
    if (layer.kind === 'text' && key && fill[key]?.trim()) layer.text = fill[key]!;
  }
  const problem = designAreaProblems({ ...config, sticker: undefined }, answer).find((p) => !['low-resolution', 'asset-unknown', 'library-not-allowed'].includes(p.code));
  if (problem) throw new Error(problem.detail);
  return answer;
}

export function configureSticker(
  config: DesignAreaConfig,
  enabled: boolean,
  required = false,
  defaults: StickerSettings = STICKER_DEFAULTS,
): DesignAreaConfig {
  if (typeof enabled !== 'boolean' || typeof required !== 'boolean' || !validStickerSettings(defaults)) throw new Error('Invalid Sticker capability');
  if (![config.sizeMm.w, config.sizeMm.h].every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0)) throw new Error('Invalid print dimensions');
  // The one place the physical limit is stated, and it is derived, not typed.
  const maxSideMm = stickerMaxSideMm();
  if (enabled && Math.max(config.sizeMm.w, config.sizeMm.h) > maxSideMm) {
    throw new Error(`Sticker supports sides up to ${maxSideMm} mm at the required contour precision`);
  }
  return { ...config, sticker: { allowed: enabled, required: enabled && required, defaults: { ...defaults } } };
}
